import * as Yup from 'yup';
import moment from 'moment';
import * as isLuhn from 'isluhn';

import { PHYSICAL_PERSON_VALUE, MORAL_PERSON_VALUE } from './constants';

const validateCardNumber = (number) => {
  return isLuhn(number) && number.toString().length >= 15;
};

const validateDateFormat = (date) => {
  return moment(date, 'DD/MM/YYYY', true).isValid();
};

const validateRFC = (rfc) => {
  const rfcTrimmed = rfc ? rfc.trim() : rfc;
  return /[A-ZÑ&]{3,4}\d{6}[A-V1-9][A-Z1-9][0-9A]/.test(rfcTrimmed);
};

const validatePostalCode = (code) => {
  return /^[0-9]{5}$/.test(code ? code.trim() : code);
};

const validateEmail = (email) => {
  // Linter disabled because regex is long
  // This is the way.
  // eslint-disable-next-line max-len
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
};

const validateCellphone = (phone) => {
  return phone.length === 10;
};

export const FORM1_VALIDATION_SCHEMA = {
  postalCode: Yup.string().required('Campo Requerido')
    .test(
      'validPC',
      'Código postal no válido',
      validatePostalCode,
    ),
};

export const FORM2_VALIDATION_SCHEMA = {
  typeVehicle: Yup.string().required('Campo Requerido'),
  brand: Yup.string().required('Campo Requerido'),
  subbrand: Yup.string().required('Campo Requerido'),
  model: Yup.string().required('Campo Requerido'),
  version: Yup.string().required('Campo Requerido'),
};

// Person type 1 = Física, person type 2 = Moral
export const FORM4_VALIDATION_SCHEMA = {
  personType: Yup.string().required('Campo Requerido'),
  name: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  firstLastname: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  secondLastname: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  birthdate: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validBirthdayFormat',
          'Fecha inválida',
          validateDateFormat,
        ),
    },
  ),
  rfc: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateRfc',
          'RFC inválido.',
          validateRFC,
        ),
    },
  ),
  // Personal Data
  email: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateEmail',
          'Correo electrónico inválido.',
          validateEmail,
        ),
    },
  ),
  cellphone: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateCellphone',
          'Debe tener 10 dígitos',
          validateCellphone,
        ),
    },
  ),
  gender: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  maritalStatus: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  occupation: Yup.string().when(
    'personType',
    {
      is: PHYSICAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  // General Moral Data
  socialReason: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  moralRfc: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateRfc',
          'RFC inválido.',
          validateRFC,
        ),
    },
  ),
  // Representative Person
  representativeName: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  representativeFirstLastname: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  representativeSecondLastname: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema.required('Campo Requerido'),
    },
  ),
  representativeEmail: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateRepresentativeEmail',
          'Correo electrónico inválido.',
          validateEmail,
        ),
    },
  ),
  representativeCellphone: Yup.string().when(
    'personType',
    {
      is: MORAL_PERSON_VALUE,
      then: (schema) => schema
        .required('Campo Requerido')
        .test(
          'validateCellphone',
          'Debe tener 10 dígitos',
          validateCellphone,
        ),
    },
  ),
  /**
   * For the future:
   * this name was chosen because the original design considered this field for the
   * Legal Representative (a real person) of the company buying the insurance.
   * At a later date, close to release, it was noted from the client
   * that this date was not about the person who represents the company
   * but rather the formation (constitution, creation) of this company.
   * But for time reasons, the form's name of the field
   * (the value used by formik and the rest of the app)
   * was not changed.
   */
  representativeBirthdate: Yup.string()
    .when(
      'personType',
      {
        is: MORAL_PERSON_VALUE,
        then: (schema) => schema
          .required('Campo Requerido')
          .test(
            'validRepresentativeBirthdateFormat',
            'Fecha inválida',
            validateDateFormat,
          ),
      },
    ),
  // Address
  postalCode: Yup.string().required('Campo requerido'),
  state: Yup.string().required('Campo requerido'),
  municipality: Yup.string().required('Campo requerido'),
  colony: Yup.string().required('Campo requerido'),
  street: Yup.string().required('Campo requerido'),
  noExt: Yup.string().required('Campo requerido'),
  // Additional information
  activity: Yup.string().required('Campo requerido'),
  sourceIncome: Yup.string().required('Campo requerido'),
  nationality: Yup.string().required('Campo requerido'),
};

export const FORM6_VALIDATION_SCHEMA = {
  serialNumber: Yup.string().required('Campo requerido'),
  engineNumber: Yup.string().required('Campo requerido'),
  plate: Yup.string().required('Campo requerido'),
  useOfTheVehicle: Yup.string().required('Campo requerido'),
  vehicleType: Yup.string().required('Campo requerido'),
  vehicleService: Yup.string().required('Campo requerido'),
};

export const FORM7_VALIDATION_SCHEMA = {
  cardName: Yup.string().required('Campo Requerido').matches(/^[a-z A-Zá-úÁ-Ú\s]+$/, 'Sin números ni caracteres especiales'),
  cardNumber: Yup.number()
    .required('Campo Requerido')
    .test(
      'validCC',
      'Número no válido',
      validateCardNumber,
    ),
  cardExpirationMonth: Yup.string().required('Campo requerido'),
  cardExpirationYear: Yup.string().required('Campo requerido'),
  cardCVV: Yup.number()
    .required('Campo Requerido')
    .moreThan(99, 'Número no válido')
    .lessThan(10000, 'Número no válido'),
  termsAndPrivacy: Yup.boolean().oneOf([true], 'Campo requerido'),
  termsAndConditons: Yup.boolean().oneOf([true], 'Campo requerido'),
  termsAndConditonsInsurance: Yup.boolean().oneOf([true], 'Campo requerido'),
};
